import { useEffect, useRef, useState } from "react";
import type { FC, ReactNode, ComponentProps } from "react";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import { useTranslation } from "@privacy/translations";
import Typography from "@mui/material/Typography";
import { CircularProgress } from "@privacy/ui";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import type { FadeProps } from "@mui/material/Fade";
import debounce from "@mui/utils/debounce";
import Fade from "@mui/material/Fade";
import visuallyHidden from "@mui/utils/visuallyHidden";
import {
  gridQuickFilterValuesSelector,
  useGridApiContext,
  useGridSelector,
  type GridValidRowModel,
} from "@mui/x-data-grid-premium";
import type { ButtonProps, SxProps } from "@mui/material";
import { Button, Stack } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { LinkWithQueryParams } from "../LinkWithQueryParams";
import {
  ArchiveIcon,
  LockIcon,
  CloudCheckIcon,
  AlertCircleIcon,
  ChevronDownIcon,
  XIcon,
  type IconWithBackgroundProps,
} from "../icons";
import type { GridProps } from "../Grid";
import { Grid } from "../Grid";
import { EmptyState } from "../EmptyState";
import { customScrollbar } from "../customScrollbar";
import { GoToSubDrawerButton, GoToSubDrawerButtonBase, GoToSubDrawerButtonsWrapper } from "./GoToSubDrawerButton";

const DRAWER_PADDING_X = 3;
const DRAWER_PADDING_Y = 3;

export type DrawerProps = {
  onClose?: () => void;
  isOpen?: boolean;
  children: ReactNode;
  width?: string;
  paperSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
};

const DRAWER_CONTENT_ELEMENT_ID = "grid-drawer-content";

export const Drawer = ({ onClose, isOpen = true, children, width, paperSx, sx }: DrawerProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && ref.current) ref.current.focus();
  }, [isOpen]);

  return (
    <MuiDrawer
      transitionDuration={0}
      elevation={3}
      variant="persistent"
      anchor="right"
      data-testid="grid-drawer"
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            bgcolor: theme => theme.palette.background.paper,
            width: `min(100vw,${width})`,
            zIndex: theme => theme.zIndex.drawer - 1,
            ...paperSx,
          },
        },
      }}
    >
      <Box
        ref={ref}
        tabIndex={-1}
        id={DRAWER_CONTENT_ELEMENT_ID}
        sx={{
          "height": "100%",
          "position": "relative",
          "&:focus": { outline: "none" },
          "overflowY": /** Always show scrollbar to avoid layout shift on followup question animations */ "scroll",
          "pb": 6,
          ...customScrollbar,
          ...sx,
        }}
      >
        {children}
      </Box>
    </MuiDrawer>
  );
};

export type DrawerHeaderProps = {
  title: string;
  renderIcon?: (props: IconWithBackgroundProps) => ReactNode;
  onClose: () => void;
  bottomSlot?: ReactNode;
  topRightSlot?: ReactNode;

  status?: StatusIndicatorProps["status"];
  archivedAt?: string;
  verticalPadding?: number;
};
const DrawerHeader: FC<DrawerHeaderProps> = ({
  title,
  renderIcon,
  onClose,
  topRightSlot,

  bottomSlot,
  status,
  archivedAt,
  verticalPadding = 3,
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      sx={{
        pt: verticalPadding ?? DRAWER_PADDING_Y,
        zIndex: 1,
        position: "sticky",
        top: "0",
        bgcolor: theme => theme.palette.background.paper,
      }}
    >
      <Stack
        direction="row-reverse"
        sx={{ pb: bottomSlot ? 2 : verticalPadding, px: DRAWER_PADDING_X, gap: 1, justifyContent: "space-between" }}
      >
        <Stack direction="row" spacing={2} sx={{ mr: "auto", order: 2, alignItems: "center" }}>
          {renderIcon && <Box sx={{ alignSelf: "flex-start" }}>{renderIcon({ size: "md" })}</Box>}
          <Typography variant="h2" sx={{ lineHeight: 1.4 }}>
            {title}

            {archivedAt ? (
              <ArchivedStatus archivedAt={archivedAt} />
            ) : (
              status !== "hidden" && <StatusIndicator status={status ?? "idle"} />
            )}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ ml: "auto", order: 0, alignItems: "center" }}>
          {topRightSlot}
          <IconButton aria-label={t("GridDrawer.CloseButton")} onClick={onClose}>
            <XIcon size={20} color={theme => theme.palette.text.primary} />
          </IconButton>
        </Stack>
      </Stack>
      {bottomSlot}
      <Box sx={{ width: "100%" }}>
        <Divider />
      </Box>
    </Stack>
  );
};

type SavingState = "saving" | "saved" | "idle" | "not-initialized";

type StatusIndicatorProps = {
  status: "readonly" | "success" | "idle" | "error" | "pending" | "hidden";
};
const StatusIndicator: FC<StatusIndicatorProps> = ({ status }) => {
  const { t } = useTranslation();
  const [savingState, setSavingState] = useState<SavingState>("not-initialized");
  const isSaving = status === "pending";

  const setSavedRef = useRef(debounce(() => setSavingState("saved"), 1000));
  const setIdleRef = useRef(debounce(() => setSavingState("idle"), 2500));

  useEffect(() => {
    if (isSaving) return setSavingState("saving");

    if (savingState === "saving") setSavedRef.current();
    if (savingState === "saved") setIdleRef.current();

    const savedRef = setSavedRef.current;
    const idleRef = setIdleRef.current;
    return () => {
      savedRef.clear();
      idleRef.clear();
    };
  }, [isSaving, savingState]);

  return (
    <Box component="span" sx={{ ml: 2 }}>
      {(() => {
        switch (status) {
          case "readonly":
            return (
              <Box
                component="span"
                sx={{ display: "inline-flex", height: "1lh", alignItems: "center", verticalAlign: "top" }}
              >
                <LockIcon size={16} color={theme => theme.palette.text.tertiary} />
              </Box>
            );
          case "error":
            return <FailedToSaveIndicator />;
          default:
            return (
              <>
                <SavingIndicatorItem timeout={{ enter: 800 }} show={savingState === "saving"}>
                  <CircularProgress size={14} />
                  <SavingIndicatorText>{t("RegistrationGrid.Drawer.Saving")}</SavingIndicatorText>
                </SavingIndicatorItem>
                <SavingIndicatorItem
                  timeout={{ enter: savingState === "not-initialized" ? 0 : 1500 }}
                  show={savingState === "not-initialized" || savingState === "idle" || savingState === "saved"}
                >
                  <CloudCheckIcon color={theme => theme.palette.text.secondary} size={14} />
                  <Fade in={savingState === "saved"}>
                    <span>
                      <SavingIndicatorText>{t("RegistrationGrid.Drawer.Saved")}</SavingIndicatorText>
                    </span>
                  </Fade>
                </SavingIndicatorItem>
              </>
            );
        }
      })()}
    </Box>
  );
};

const ArchivedStatus = ({ archivedAt }: { archivedAt: string }) => {
  const { t, formatDate } = useTranslation();
  return (
    <Box component="span" sx={{ ml: 2, display: "inline-flex", alignItems: "center", gap: 1, verticalAlign: "middle" }}>
      <ArchiveIcon size={14} color={theme => theme.palette.text.tertiary} />
      <Typography
        component="span"
        sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}
      >
        {`${t("GridDrawer.Header.Archived")} (${formatDate(archivedAt)})`}
      </Typography>
    </Box>
  );
};

const SavingIndicatorText: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), transform: "translateY(1px)" }}>
      {children}
    </Typography>
  );
};

const SavingIndicatorItem: FC<{
  children: ReactNode;
  show: boolean;
  timeout?: FadeProps["timeout"];
}> = ({ children, show, timeout }) => {
  if (!show) return null;

  return (
    <Fade timeout={timeout} appear={true} in={show}>
      <Box component="span" sx={{ display: "inline-flex", gap: 1, alignItems: "center" }}>
        {children}
      </Box>
    </Fade>
  );
};

const FailedToSaveIndicator = () => {
  const { t } = useTranslation();

  return (
    <Fade in unmountOnExit>
      <span>
        <AlertCircleIcon size={16} color={theme => theme.palette.error.main} />
        <Box role="alert" sx={visuallyHidden}>
          {t("DynamicForm.Inline.FailedToSave")}
        </Box>
      </span>
    </Fade>
  );
};

const DrawerLoading = () => (
  <Stack
    sx={{
      height: "70vh",
      width: "100%",
      alignItems: "center",
      justifyContent: "center",
      transform: "translateY(-10%)",
    }}
  >
    <CircularProgress delay={1000} />
  </Stack>
);

const DrawerGrid = <Row extends GridValidRowModel>({
  showColumnHeaders,
  partsSx,
  slots,
  ...props
}: GridProps<Row> & { showColumnHeaders?: boolean }) => {
  return (
    <Grid
      showToolbar={slots?.toolbar !== undefined}
      slots={{ noResultsOverlay: GridNoResultsOverlay, ...slots }}
      getRowHeight={() => "auto"}
      disableColumnMenu
      columnHeaderHeight={showColumnHeaders ? 35 : 0}
      disableColumnReorder
      disableAutosize
      disableColumnResize
      disableRowSelectionOnClick
      hideFooter
      sortingOrder={props.sortingOrder || ["desc", "asc"]}
      partsSx={{
        mainSx: { borderLeft: "none", borderRight: "none", height: "100%", ...partsSx?.mainSx },
        columnHeaderSx: { pl: DRAWER_PADDING_X },
        cellSx: { pl: DRAWER_PADDING_X },

        ...(!showColumnHeaders && { columnHeadersSx: { display: "none" } }),
      }}
      {...props}
    />
  );
};

const GridToolbar: FC<{ children?: ReactNode; sx?: SxProps<Theme> }> = ({ children, sx }) => {
  const gridApiRef = useGridApiContext();
  const { t } = useTranslation();
  return (
    <Stack sx={{ pb: 3, px: 4, alignItems: "flex-end", flexDirection: "row", ...sx }}>
      <Grid.SearchField gridApiRef={gridApiRef} placeholder={t("Drawer.SubDrawer.GridSearchPlaceholder")} />
      {children}
    </Stack>
  );
};

const GridNoRowsOverlay: FC<ComponentProps<typeof EmptyState>> = props => {
  return <EmptyState {...props} />;
};

const GridNoResultsOverlay = () => {
  const { t } = useTranslation();
  const gridApiRef = useGridApiContext();
  const query = (useGridSelector(gridApiRef, gridQuickFilterValuesSelector) ?? []).join(" ");

  return (
    <EmptyState
      label={t("Grid.NoResults.Search", { query })}
      action={
        <Button variant="text" onClick={() => gridApiRef.current.setQuickFilterValues([])}>
          {t("Grid.ClearSearch")}
        </Button>
      }
    />
  );
};

const GridCell: FC<{ children: ReactNode; align?: "flex-start" | "center" }> = ({ children, align = "flex-start" }) => {
  return <Box sx={{ width: "100%", alignSelf: align, py: 2 }}>{children}</Box>;
};

const Content: FC<{ children: ReactNode; sx?: SxProps }> = ({ children, sx }) => {
  return (
    <Stack
      sx={[
        {
          px: Drawer.PADDING_X,
          py: 5,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Stack>
  );
};

type SubDrawerHeaderProps = {
  renderIcon?: (props: IconWithBackgroundProps) => ReactNode;
  title: string | ReactNode;
  customSlot?: ReactNode;
  onBackClick: () => void;
  children?: ReactNode;
  sx?: SxProps<Theme>;
} & ({ count: number; countAsterisk?: boolean } | { count?: undefined; countAsterisk?: undefined });

const BACK_BUTTON_PADDING = 1.5;
const SubDrawerHeader: FC<SubDrawerHeaderProps> = ({
  renderIcon,
  title,
  customSlot,
  onBackClick,
  children,
  count,
  countAsterisk,
  sx,
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      sx={{
        flexDirection: "row",
        px: DRAWER_PADDING_X,
        width: "100%",
        py: 3,
        bgcolor: theme => theme.palette.background.paper,
        alignItems: "center",
        ...sx,
      }}
    >
      <Stack sx={{ gap: 2, width: "100%" }}>
        <Stack direction="row" sx={{ gap: 2 }}>
          <IconButton
            sx={{ p: BACK_BUTTON_PADDING, ml: -BACK_BUTTON_PADDING }}
            onClick={onBackClick}
            aria-label={t("GridDrawer.SubDrawer.Back")}
          >
            <ChevronDownIcon size={14} rotate={90} />
          </IconButton>
          <Stack direction="row" sx={{ alignItems: "center" }}>
            {renderIcon && <Box sx={{ mr: 2 }}>{renderIcon({ size: "sm" })}</Box>}
            {typeof title === "string" ? (
              <Typography variant="h3" sx={{ fontWeight: "600" }}>
                {title}
              </Typography>
            ) : (
              title
            )}
            {typeof count === "number" && (
              <Typography component="span" variant="h3" sx={{ ml: 1, fontWeight: "400" }}>
                ({count}
                {countAsterisk && " *"})
              </Typography>
            )}
          </Stack>
        </Stack>
        {customSlot}
      </Stack>
      {children}
    </Stack>
  );
};

const DrawerTabButtons: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Stack direction="row" spacing={1} sx={{ minHeight: 32 }}>
      {children}
    </Stack>
  );
};

const DrawerTabButton = ({
  active,
  sx,
  children,
  onHover,
  ...props
}: ComponentProps<typeof LinkWithQueryParams> & ButtonProps & { active: boolean; onHover?: () => void }) => {
  return (
    <Button
      component={LinkWithQueryParams}
      onMouseOver={onHover}
      {...props}
      variant="text"
      sx={{
        "color": theme => theme.palette.text.primary,
        "borderRadius": "0",
        "borderBottom": "2px solid transparent",
        "py": 1,
        "px": 0.5,
        "transform": "translateY(1px)",
        "&.MuiButtonBase-root": { backgroundColor: "transparent" },
        "&:hover, &.Mui-focusVisible": {
          borderBottom: theme => `2px solid ${theme.palette.grey["400"]}`,
          backgroundColor: "transparent",
        },
        ...(active && {
          color: theme => theme.palette.text.primary,
          fontWeight: 600,
          borderColor: theme => theme.palette.primary.main,
          pointerEvents: "none",
        }),
        ...sx,
      }}
    >
      <Box component="span" sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        {children}
        <Box component="span" aria-hidden sx={{ height: 0, overflow: "hidden", visibility: "hidden", fontWeight: 600 }}>
          {children}
        </Box>
      </Box>
    </Button>
  );
};

const DrawerDivider = () => <Divider sx={{ mx: -Drawer.PADDING_X }} />;

Drawer.GridCell = GridCell;
Drawer.GridToolbar = GridToolbar;
Drawer.GridNoRowsOverlay = GridNoRowsOverlay;
Drawer.Loading = DrawerLoading;
Drawer.Header = DrawerHeader;
Drawer.SubDrawerHeader = SubDrawerHeader;
Drawer.GoToSubDrawerButton = GoToSubDrawerButton;
Drawer.GoToSubDrawerButtonBase = GoToSubDrawerButtonBase;
Drawer.GoToSubDrawerButtonsWrapper = GoToSubDrawerButtonsWrapper;
Drawer.Content = Content;
Drawer.Divider = DrawerDivider;
Drawer.PADDING_X = DRAWER_PADDING_X;
Drawer.CONTENT_ELEMENT_ID = DRAWER_CONTENT_ELEMENT_ID;
Drawer.Grid = DrawerGrid;
Drawer.TabButton = DrawerTabButton;
Drawer.TabButtons = DrawerTabButtons;
